import React, { useState } from 'react';
import { format } from 'date-fns';
import ContestFormModal from './ContestFormModal';
import ParticipantsModal from './ParticipantsModal';

const ContestTable = ({ contests, loading, onUpdate, fetchContests }) => {
  const [selectedContest, setSelectedContest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);

  const handleUpdateClick = (contest) => {
    const modifiedContest = {
      ...contest,
      companyId: contest.createdBy?.id
    };
    setSelectedContest(modifiedContest);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedContest(null);
  };

  const handleParticipantsClick = (contest) => {
    setSelectedContest(contest);
    setIsParticipantsModalOpen(true);
  };

  const closeParticipantsModal = () => {
    setIsParticipantsModalOpen(false);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (contests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-8 text-center">
          <p className="text-gray-500 text-lg">No contests found.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Contest Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category / Subcategory
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dates
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sponsor
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prizes
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contests.map((contest) => (
              <tr key={contest.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{contest.heading}</div>
                  <div className="text-sm text-gray-500 truncate max-w-xs" title={contest.requirement}>
                    {contest.requirement}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{contest.category}</div>
                  <div className="text-sm text-gray-500">{contest.subcategory}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">Start: {formatDate(contest.submissionStartDate)}</div>
                  <div className="text-sm text-gray-500">End: {formatDate(contest.submissionEndDate)}</div>
                  <div className="text-sm text-gray-500">Result: {formatDate(contest.resultDate)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    contest.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {contest.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{contest.createdBy?.name || 'Unknown'}</div>
                  <div className="text-sm text-gray-500">{contest.createdBy?.email || ''}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {contest.prizes.map((prize) => (
                      <div key={prize.id || prize.position}>
                        {prize.position}. ${prize.amount}
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleUpdateClick(contest)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Update
                    </button>
                    <button
                      onClick={() => handleParticipantsClick(contest)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Participants
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {contests.map((contest) => (
          <div key={contest.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">{contest.heading}</h3>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  contest.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {contest.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500 line-clamp-2">{contest.requirement}</p>
              
              <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Category:</span>
                  <p className="text-gray-900">{contest.category}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Subcategory:</span>
                  <p className="text-gray-900">{contest.subcategory}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Start Date:</span>
                  <p className="text-gray-900">{formatDate(contest.submissionStartDate)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">End Date:</span>
                  <p className="text-gray-900">{formatDate(contest.submissionEndDate)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Result Date:</span>
                  <p className="text-gray-900">{formatDate(contest.resultDate)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Sponsor:</span>
                  <p className="text-gray-900">{contest.createdBy?.name || 'Unknown'}</p>
                </div>
              </div>
              
              <div className="mt-4">
                <span className="font-medium text-gray-500">Prizes:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {contest.prizes.map((prize) => (
                    <div key={prize.id || prize.position} className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {prize.position}. ${prize.amount}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  onClick={() => handleUpdateClick(contest)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Update
                </button>
                <button
                  onClick={() => handleParticipantsClick(contest)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Participants
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Contest Update Modal */}
      {showModal && (
        <ContestFormModal
          contest={selectedContest}
          onClose={handleCloseModal}
          onSave={(contestData) => {
            onUpdate(selectedContest.id, contestData);
            setShowModal(false);
          }}
        />
      )}

      {/* Participants Modal */}
      <ParticipantsModal
        isOpen={isParticipantsModalOpen}
        onClose={closeParticipantsModal}
        contest={selectedContest}
      />
    </>
  );
};

export default ContestTable; 